import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Route, Sparkles, Check, Users, Layers, Database, ArrowDown } from "lucide-react";
import { Logo } from "@/components/Logo";

// "Request a Demo" enquiries route here. PLACEHOLDER — update to your real inbox.
const DEMO_MAILTO = "mailto:hello@getministryhub.com?subject=Request%20a%20Demo";

// Connect checkout entry (existing signup → Stripe Connect flow).
const CONNECT_SIGNUP = "/signup?plan=connect&interval=monthly";

// Approved testimonial (verbatim). No name/church/photo per approval.
const TESTIMONIAL_QUOTE = "First Impressions should be about people, not managing technology. MinistryHub gives our volunteers a simple, welcoming way to help someone take a next step, while still connecting that information back to the systems our team already uses. It solves a real gap between the experience we want to create for guests and the tools we need behind the scenes.";

const IMG = {
  hero: "https://images.unsplash.com/photo-1651514644627-8a06b0559587?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&ixlib=rb-4.1.0",
  hope: "https://images.unsplash.com/photo-1777489689168-497290f235d5?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400&fit=crop&ixlib=rb-4.1.0",
};

const PROBLEM_CARDS = [
  { icon: MapPin, title: "Too many places to go", body: "Give people one clear place to start." },
  { icon: Route, title: "Disconnected next steps", body: "Turn interest into intentional action." },
  { icon: Sparkles, title: "Technology people have to figure out", body: "Create experiences that feel welcoming, simple, and human." },
];

const CONNECT_FEATURES = [
  "Welcome Kiosk", "Unlimited guests", "Connect Cards", "Responses", "Experiences",
  "QR codes", "Church branding", "Email notifications", "Basic insights", "Planning Center connection",
];

const MH_COLUMN = ["Welcome experiences", "Connect Cards", "Responses", "Next-step interactions", "Church-branded experiences"];
const PCO_COLUMN = ["People records", "Church database", "Workflows", "Follow-up data", "Ministry administration"];

const TIERS = [
  {
    key: "connect", name: "Connect", status: "Available Now", available: true,
    heading: "Create the experience.",
    body: "Help people feel welcomed, respond, and take their next step.",
    monthly: 50, annual: 500,
  },
  {
    key: "grow", name: "Grow", status: "Coming Soon", available: false,
    heading: "Follow through intentionally.",
    body: "Help ministry teams organize next steps, follow up with people, and build clearer pathways.",
    monthly: 100, annual: 1000,
  },
  {
    key: "multiply", name: "Multiply", status: "Coming Soon", available: false,
    heading: "See the bigger picture.",
    body: "Give church leaders greater visibility across ministries, campuses, engagement, and discipleship movement.",
    monthly: 200, annual: 2000,
  },
];

const STANDARDS = [
  "Beautiful enough to feel intentional.",
  "Simple enough for volunteers to use.",
  "Connected enough to eliminate unnecessary work.",
  "Focused enough to stay out of the way of ministry.",
];

const Section = ({ children, className = "", ...rest }) => (
  <section className={`px-6 md:px-12 lg:px-20 ${className}`} {...rest}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <p className="text-sm uppercase tracking-[0.2em] text-[var(--mh-gold)] font-medium">{children}</p>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-[var(--mh-soft-white)] text-[var(--mh-charcoal)] antialiased">
      {/* Nav */}
      <nav className="px-6 md:px-12 lg:px-20 py-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <Logo theme="black" height={30} />
          </Link>
          <div className="hidden lg:flex items-center gap-7 text-sm text-[var(--mh-muted-text)]">
            <a href="#why" className="hover:text-[var(--mh-charcoal)] transition-colors">Why MinistryHub</a>
            <a href="#planning-center" className="hover:text-[var(--mh-charcoal)] transition-colors">Planning Center</a>
            <a href="#whats-coming" className="hover:text-[var(--mh-charcoal)] transition-colors">What&rsquo;s Coming</a>
            <Link to="/pricing" className="hover:text-[var(--mh-charcoal)] transition-colors" data-testid="landing-pricing-link">Pricing</Link>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm text-[var(--mh-muted-text)] hover:text-[var(--mh-charcoal)] hidden sm:inline px-3 py-2 transition-colors" data-testid="landing-login-link">Sign In</Link>
            <Link to="/pricing" data-testid="landing-cta-signup">
              <Button className="bg-[var(--mh-charcoal)] hover:bg-black text-white rounded-full px-5 h-10 transition-colors">Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* 1. Hero */}
      <Section className="pt-16 md:pt-24 pb-20 md:pb-24">
        <div className="max-w-3xl fade-up">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-[var(--mh-border)] text-xs uppercase tracking-[0.18em] text-[var(--mh-muted-text)] mb-8 mh-shadow-soft">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--mh-gold)]" /> Built for the local church
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08]">
            Make the next step easier to take.
          </h1>
          <p className="mt-7 text-lg md:text-xl text-[var(--mh-muted-text)] leading-relaxed max-w-2xl">
            MinistryHub helps churches create simple, welcoming digital experiences that remove barriers between people and their next steps with Jesus.
          </p>
          <p className="mt-4 text-base text-[var(--mh-charcoal)]">
            Start with <span className="font-semibold">Connect</span> today. Grow and Multiply are coming next.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Link to="/pricing" data-testid="hero-cta-get-started">
              <Button className="bg-[var(--mh-charcoal)] hover:bg-black text-white h-12 px-7 rounded-full text-base transition-colors">
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="#what-is" data-testid="hero-cta-how-it-works">
              <Button variant="outline" className="h-12 px-7 rounded-full text-base border-[var(--mh-charcoal)] text-[var(--mh-charcoal)] bg-transparent hover:bg-[var(--mh-charcoal)] hover:text-white transition-colors">
                See How It Works
              </Button>
            </a>
          </div>
        </div>

        <div className="mt-14 rounded-[var(--mh-radius)] overflow-hidden mh-shadow-card aspect-[16/7]">
          <img src={IMG.hero} alt="People talking and sharing coffee in a church lobby" loading="eager" decoding="async" className="w-full h-full object-cover" />
        </div>
      </Section>

      {/* 2. The Problem */}
      <Section className="py-20 md:py-24 bg-white border-y border-[var(--mh-border)]">
        <div className="max-w-3xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Church technology should make ministry easier.</h2>
          <div className="mt-6 space-y-4 text-lg text-[var(--mh-muted-text)] leading-relaxed">
            <p>Churches already use great tools to manage people, registrations, groups, giving, and ministry. But the experience people encounter on the other side can still feel fragmented.</p>
            <p>A first-time guest shouldn&rsquo;t need to understand your church database. A volunteer shouldn&rsquo;t need to know which software runs behind the scenes. A person trying to take a next step shouldn&rsquo;t have to figure out where to go.</p>
            <p className="text-[var(--mh-charcoal)]">MinistryHub creates a simple experience in front of the systems your church already uses.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {PROBLEM_CARDS.map((c) => (
            <div key={c.title} className="p-7 rounded-[var(--mh-radius)] bg-[var(--mh-soft-white)] border border-[var(--mh-border)]">
              <div className="w-11 h-11 rounded-xl bg-white border border-[var(--mh-border)] flex items-center justify-center">
                <c.icon className="w-5 h-5 text-[var(--mh-gold)]" />
              </div>
              <h3 className="mt-5 font-semibold text-lg">{c.title}</h3>
              <p className="mt-2 text-[var(--mh-muted-text)] leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-12 text-xl md:text-2xl font-medium tracking-tight max-w-3xl">
          Technology should fade into the background so ministry can move forward.
        </p>
      </Section>

      {/* 3. What MinistryHub Is */}
      <Section id="what-is" className="py-24 md:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">The experience layer for your church.</h2>
            <p className="mt-6 text-lg text-[var(--mh-muted-text)] leading-relaxed">
              MinistryHub isn&rsquo;t trying to replace the tools your team already trusts. It gives your church a beautiful, intentional layer in front of them&mdash;designed around the people using it, not the database behind it.
            </p>
            <p className="mt-4 text-[var(--mh-charcoal)] font-medium">
              Welcome &bull; Connect &bull; Respond &bull; Take a Next Step
            </p>
          </div>
          {/* Visual flow */}
          <div className="flex flex-col items-center gap-3">
            <div className="w-full text-center rounded-2xl bg-white border border-[var(--mh-border)] px-6 py-5 mh-shadow-soft">
              <Users className="w-5 h-5 mx-auto text-[var(--mh-gold)]" />
              <p className="mt-2 font-semibold">People</p>
            </div>
            <ArrowDown className="w-5 h-5 text-[var(--mh-muted-text)]" />
            <div className="w-full text-center rounded-2xl bg-[var(--mh-charcoal)] text-white px-6 py-6 mh-shadow-card">
              <Layers className="w-5 h-5 mx-auto text-[var(--mh-gold)]" />
              <p className="mt-2 font-semibold text-lg">MinistryHub</p>
              <p className="mt-1 text-sm text-white/70">Welcome &bull; Connect &bull; Respond &bull; Next Step</p>
            </div>
            <ArrowDown className="w-5 h-5 text-[var(--mh-muted-text)]" />
            <div className="w-full text-center rounded-2xl bg-white border border-[var(--mh-border)] px-6 py-5 mh-shadow-soft">
              <Database className="w-5 h-5 mx-auto text-[var(--mh-gold)]" />
              <p className="mt-2 font-semibold">The systems your church already trusts</p>
            </div>
          </div>
        </div>
      </Section>

      {/* 4. Planning Center */}
      <Section id="planning-center" className="py-24 md:py-28 bg-[var(--mh-navy)] text-white">
        <div className="max-w-3xl">
          <Eyebrow>Built to work together</Eyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">MinistryHub + Planning Center</h2>
          <div className="mt-6 space-y-4 text-lg text-white/75 leading-relaxed">
            <p>Keep Planning Center as your church&rsquo;s trusted people database. MinistryHub gives people a better way to interact with it.</p>
            <p>MinistryHub Connect creates intentional guest and next-step experiences, then sends meaningful information into Planning Center&mdash;creating or updating people from Connect Card submissions and matching existing people by email, so your team avoids another disconnected people database.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          <div className="p-8 rounded-[var(--mh-radius)] bg-white/5 border border-white/15">
            <p className="font-semibold text-lg">MinistryHub</p>
            <p className="text-sm text-white/60">The experience people see.</p>
            <ul className="mt-5 space-y-2.5">
              {MH_COLUMN.map((f) => (
                <li key={f} className="flex items-center gap-3"><Check className="w-4 h-4 text-[var(--mh-gold)] shrink-0" /><span className="text-white/85">{f}</span></li>
              ))}
            </ul>
          </div>
          <div className="p-8 rounded-[var(--mh-radius)] bg-white/5 border border-white/15">
            <p className="font-semibold text-lg">Planning Center</p>
            <p className="text-sm text-white/60">The system your team already trusts.</p>
            <ul className="mt-5 space-y-2.5">
              {PCO_COLUMN.map((f) => (
                <li key={f} className="flex items-center gap-3"><Check className="w-4 h-4 text-white/50 shrink-0" /><span className="text-white/85">{f}</span></li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-10 text-xl md:text-2xl font-medium tracking-tight">A better front door. The same trusted database.</p>
      </Section>

      {/* 5. Connect — Available Today */}
      <Section className="py-24 md:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <Eyebrow>Available now</Eyebrow>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">Start welcoming people well.</h2>
            <p className="mt-6 text-lg text-[var(--mh-muted-text)] leading-relaxed">
              Connect gives churches the tools to create intentional first impressions and clear next steps&mdash;without rebuilding their entire technology stack.
            </p>
            <p className="mt-6">
              <span className="text-4xl font-bold text-[var(--mh-charcoal)]">$50</span>
              <span className="text-[var(--mh-muted-text)]"> /month</span>
              <span className="text-[var(--mh-muted-text)]"> &nbsp;or&nbsp; </span>
              <span className="text-2xl font-bold text-[var(--mh-charcoal)]">$500</span>
              <span className="text-[var(--mh-muted-text)]"> /year</span>
            </p>
            <p className="mt-1 text-xs text-[var(--mh-muted-text)]">Applicable taxes may be added at checkout based on location.</p>
            <Link to={CONNECT_SIGNUP} className="inline-block mt-7" data-testid="connect-cta-start">
              <Button className="bg-[var(--mh-charcoal)] hover:bg-black text-white h-12 px-7 rounded-full text-base transition-colors">
                Start with Connect <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {CONNECT_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-3 rounded-2xl bg-white border border-[var(--mh-border)] px-5 py-4 mh-shadow-soft">
                <Check className="w-4 h-4 text-[var(--mh-green)] shrink-0" />
                <span className="text-[var(--mh-charcoal)]">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* 6. The Bigger Vision */}
      <Section className="py-24 md:py-28 bg-white border-y border-[var(--mh-border)]">
        <div className="max-w-3xl">
          <Eyebrow>This is just the start</Eyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">From first step to faithful follow-through.</h2>
          <div className="mt-6 space-y-4 text-lg text-[var(--mh-muted-text)] leading-relaxed">
            <p>Helping someone take a next step matters. Making sure that step doesn&rsquo;t get lost matters too.</p>
            <p className="text-[var(--mh-charcoal)] font-medium text-xl">MinistryHub is built around a simple belief: every meaningful action deserves intentional follow-up.</p>
            <p>Today, <span className="text-[var(--mh-charcoal)] font-medium">Connect</span> helps churches create the experience where those actions happen.</p>
            <p><span className="text-[var(--mh-charcoal)] font-medium">Grow</span> will help ministry teams intentionally follow up and guide people toward their next steps.</p>
            <p><span className="text-[var(--mh-charcoal)] font-medium">Multiply</span> will help church leaders understand the bigger picture&mdash;where people are moving, where ministry is healthy, and where people may be getting stuck.</p>
          </div>
        </div>
      </Section>

      {/* 7. Three-Tier Journey */}
      <Section id="whats-coming" className="py-24 md:py-28">
        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.map((t) => (
            <div key={t.key} className={`flex flex-col p-8 rounded-[var(--mh-radius)] bg-white mh-shadow-card ${t.available ? "border border-[var(--mh-border)]" : "border border-dashed border-[var(--mh-border)]"}`} data-testid={`journey-${t.key}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold tracking-tight">{t.name}</h3>
                {t.available ? (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[var(--mh-green)] text-white font-medium">{t.status}</span>
                ) : (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[var(--mh-surface)] text-[var(--mh-muted-text)] border border-[var(--mh-border)] font-medium">{t.status}</span>
                )}
              </div>
              <p className="mt-4 font-semibold uppercase tracking-wide text-sm text-[var(--mh-gold)]">{t.heading}</p>
              <p className="mt-2 text-[var(--mh-muted-text)] leading-relaxed flex-1">{t.body}</p>
              <p className="mt-6">
                <span className="text-3xl font-bold text-[var(--mh-charcoal)]">${t.monthly}</span>
                <span className="text-[var(--mh-muted-text)]">/mo</span>
                <span className="text-[var(--mh-muted-text)] text-sm"> &middot; ${t.annual.toLocaleString()}/yr</span>
              </p>
              {t.available ? (
                <Link to="/pricing" className="mt-6" data-testid={`journey-${t.key}-cta`}>
                  <Button className="w-full h-11 rounded-full bg-[var(--mh-charcoal)] hover:bg-black text-white transition-colors">Get Started</Button>
                </Link>
              ) : (
                <Button disabled className="mt-6 w-full h-11 rounded-full bg-[var(--mh-surface)] text-[var(--mh-muted-text)] border border-[var(--mh-border)] cursor-not-allowed" data-testid={`journey-${t.key}-comingsoon`}>Coming Soon</Button>
              )}
            </div>
          ))}
        </div>
        {/* Progression */}
        <div className="mt-12 grid sm:grid-cols-3 gap-4 items-stretch">
          {[
            { name: "Connect", line: "Someone took a step." },
            { name: "Grow", line: "Someone followed up." },
            { name: "Multiply", line: "Leadership can see the movement." },
          ].map((s, i) => (
            <div key={s.name} className="relative rounded-2xl bg-[var(--mh-surface)] border border-[var(--mh-border)] px-6 py-5 text-center">
              <p className="font-semibold">{s.name}</p>
              <p className="mt-1 text-sm text-[var(--mh-muted-text)]">{s.line}</p>
              {i < 2 && <ArrowRight className="hidden sm:block absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--mh-muted-text)] z-10 bg-[var(--mh-soft-white)] rounded-full" />}
            </div>
          ))}
        </div>
      </Section>

      {/* 8. Why MinistryHub Exists */}
      <Section id="why" className="py-24 md:py-28 bg-[var(--mh-surface)]">
        <div className="max-w-3xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Built for ministry. Not for more software.</h2>
          <p className="mt-6 text-lg text-[var(--mh-muted-text)] leading-relaxed">
            Church teams don&rsquo;t need another system to babysit. They need technology that removes friction, works with the tools they already trust, and helps people move toward Jesus.
          </p>
          <ul className="mt-8 space-y-3">
            {STANDARDS.map((s) => (
              <li key={s} className="flex items-center gap-3 text-lg text-[var(--mh-charcoal)]">
                <Check className="w-5 h-5 text-[var(--mh-green)] shrink-0" /> {s}
              </li>
            ))}
          </ul>
        </div>
        <p className="mt-12 text-2xl md:text-3xl font-bold tracking-tight max-w-4xl">
          Building technology that removes barriers between people and their next steps with Jesus.
        </p>
      </Section>

      {/* 9. Testimonial */}
      <Section className="py-24 md:py-28">
        <div className="max-w-3xl mx-auto text-center">
          <Sparkles className="w-6 h-6 mx-auto text-[var(--mh-gold)]" />
          <blockquote className="mt-6 text-2xl md:text-3xl font-medium tracking-tight leading-snug text-[var(--mh-charcoal)]" data-testid="testimonial-quote">
            &ldquo;{TESTIMONIAL_QUOTE}&rdquo;
          </blockquote>
          <p className="mt-6 font-semibold text-[var(--mh-charcoal)]" data-testid="testimonial-attribution">Adult Ministries Pastor</p>
          <p className="text-sm text-[var(--mh-charcoal)]/60" data-testid="testimonial-location">Prescott, AZ</p>
        </div>
      </Section>

      {/* 10. Final CTA */}
      <Section className="py-24 md:py-28 bg-[var(--mh-charcoal)] text-white">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">Make the next step easier to take.</h2>
            <p className="mt-5 text-lg text-white/75 leading-relaxed">
              Start with MinistryHub Connect today and give your church a simpler, more intentional way to welcome people and help them move forward.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to={CONNECT_SIGNUP} data-testid="final-cta-get-started">
                <Button className="bg-white text-[var(--mh-charcoal)] hover:bg-[var(--mh-surface)] h-12 px-7 rounded-full text-base transition-colors">
                  Get Started &mdash; $50/month <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing" data-testid="final-cta-view-pricing">
                <Button variant="outline" className="h-12 px-7 rounded-full text-base border-white/40 text-white bg-transparent hover:bg-white hover:text-[var(--mh-charcoal)] transition-colors">
                  View Pricing
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-white/60">No long-term contract. Cancel anytime.</p>
          </div>
          <div className="rounded-[var(--mh-radius)] overflow-hidden mh-shadow-card aspect-[4/3]">
            <img src={IMG.hope} alt="People enjoying community together outdoors in warm light" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="px-6 md:px-12 lg:px-20 py-14 bg-[var(--mh-soft-white)] border-t border-[var(--mh-border)]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between gap-8">
          <div className="max-w-md">
            <Logo theme="black" height={28} />
            <p className="mt-4 text-[var(--mh-muted-text)] leading-relaxed">
              Helping churches remove barriers so people can take their next step with Jesus.
            </p>
            <p className="mt-3 text-sm text-[var(--mh-muted-text)]">Designed by church leaders, for church leaders.</p>
          </div>
          <div className="flex flex-col gap-2 text-sm text-[var(--mh-muted-text)]">
            <Link to="/pricing" className="hover:text-[var(--mh-charcoal)] transition-colors">Pricing</Link>
            <Link to="/login" className="hover:text-[var(--mh-charcoal)] transition-colors">Sign In</Link>
            <Link to="/pricing" className="hover:text-[var(--mh-charcoal)] transition-colors">Get Started</Link>
            <a href={DEMO_MAILTO} className="hover:text-[var(--mh-charcoal)] transition-colors">Request a Demo</a>
            <Link to="/terms" className="hover:text-[var(--mh-charcoal)] transition-colors" data-testid="landing-footer-terms">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-[var(--mh-charcoal)] transition-colors" data-testid="landing-footer-privacy">Privacy Policy</Link>
          </div>
        </div>
        <p className="max-w-6xl mx-auto mt-10 text-xs text-[var(--mh-muted-text)]">© {new Date().getFullYear()} MinistryHub. Made for the local church.</p>
      </footer>
    </div>
  );
}
